import { GUI } from 'dat.gui'
import { BlendFunction, ToneMappingEffect, ToneMappingMode } from 'postprocessing'
import { WebGLRenderer } from 'three'

import { StoreScheme } from '../../../shared/store/store'
import { ToneMapping } from '../../../../../trillion-common/src/objects/posteffects/tone-mapping.object'
import { ToneMappingDto } from '../../../../../trillion-common/src/dto/posteffects/tone-mapping.dto'

export function addTonemappingGUI(
  gui: GUI,
  renderer: WebGLRenderer,
  toneMappingEffect: ToneMappingEffect,
  toneMappingConfig: ToneMappingDto | undefined,
  store: StoreScheme,
) {
  const setToneMapping = store.getState().setToneMapping

  const tonemappingFolder = gui.addFolder('ToneMapping')
  tonemappingFolder.open()

  const toneMappingOptions = {
    mode: toneMappingEffect.mode,
    exposure: renderer.toneMappingExposure,
    resolution: toneMappingEffect.resolution,
    whitePoint: toneMappingEffect.uniforms.get('whitePoint')?.value as number,
    middleGrey: toneMappingEffect.uniforms.get('middleGrey')?.value as number,

    averageLuminance: toneMappingEffect.uniforms.get('averageLuminance')?.value as number,
    minLuminance: toneMappingEffect.adaptiveLuminanceMaterial.uniforms.minLuminance.value as number,

    adaptationRate: toneMappingEffect.adaptiveLuminanceMaterial.adaptationRate,
    opacity: toneMappingConfig?.opacity ?? (toneMappingEffect.blendMode.opacity.value as number),
    blendFunction: toneMappingEffect.blendMode.blendFunction,
  }

  tonemappingFolder.add(toneMappingOptions, 'mode', ToneMappingMode).onChange((value) => {
    const numValue = Number(value)
    toneMappingEffect.mode = numValue
    updateToneMappingEffect((toneMapping) => {
      toneMapping.mode = numValue
    })
  })
  tonemappingFolder
    .add(toneMappingOptions, 'exposure', 0.0, 2.0, 0.001)
    .onChange((value: number) => {
      renderer.toneMappingExposure = value
      updateToneMappingEffect((toneMapping) => {
        toneMapping.exposure = value
      })
    })
  tonemappingFolder
    .add(toneMappingOptions, 'whitePoint', 2.0, 32.0, 0.01)
    .name('white point')
    .onChange((value: number) => {
      ;(toneMappingEffect.uniforms.get('whitePoint') as { value: number }).value = value
      updateToneMappingEffect((toneMapping) => {
        toneMapping.whitePoint = value
      })
    })
  tonemappingFolder
    .add(toneMappingOptions, 'middleGrey', 0.0, 1.0, 0.0001)
    .name('middle grey')
    .onChange((value: number) => {
      ;(toneMappingEffect.uniforms.get('middleGrey') as { value: number }).value = value
      updateToneMappingEffect((toneMapping) => {
        toneMapping.middleGrey = value
      })
    })

  tonemappingFolder
    .add(toneMappingOptions, 'averageLuminance', 0.0001, 1.0, 0.0001)
    .name('average luminance')
    .onChange((value: number) => {
      ;(toneMappingEffect.uniforms.get('averageLuminance') as { value: number }).value = value
      updateToneMappingEffect((toneMapping) => {
        toneMapping.averageLuminance = value
      })
    })
  tonemappingFolder
    .add(toneMappingOptions, 'resolution', [64, 128, 256, 512])
    .onChange((value: number) => {
      const numValue = Number(value)
      toneMappingEffect.resolution = numValue
      updateToneMappingEffect((toneMapping) => {
        toneMapping.resolution = numValue
      })
    })

  tonemappingFolder
    .add(toneMappingOptions, 'adaptationRate', 0.001, 3.0, 0.001)
    .name('adaptation rate')
    .onChange((value: number) => {
      toneMappingEffect.adaptiveLuminanceMaterial.adaptationRate = value
      updateToneMappingEffect((toneMapping) => {
        toneMapping.adaptationRate = value
      })
    })
  tonemappingFolder
    .add(toneMappingOptions, 'minLuminance', 0.001, 1.0, 0.001)
    .name('min luminance')
    .onChange((value: number) => {
      toneMappingEffect.adaptiveLuminanceMaterial.uniforms.minLuminance.value = value
      updateToneMappingEffect((toneMapping) => {
        toneMapping.minLuminance = value
      })
    })

  tonemappingFolder.add(toneMappingOptions, 'opacity', 0.0, 1.0, 0.01).onChange((value: number) => {
    toneMappingEffect.blendMode.opacity.value = value
    updateToneMappingEffect((toneMapping) => {
      toneMapping.opacity = value
    })
  })
  tonemappingFolder
    .add(toneMappingOptions, 'blendFunction', BlendFunction)
    .name('blend mode')
    .onChange((value) => {
      const numValue = Number(value)
      toneMappingEffect.blendMode.blendFunction = numValue
      updateToneMappingEffect((toneMapping) => {
        toneMapping.blendFunction = numValue
      })
    })

  function updateToneMappingEffect(updateCallback: (toneMapping: ToneMapping) => void) {
    updateCallback(toneMappingOptions)
    setToneMapping(toneMappingOptions)
  }

  setToneMapping(toneMappingOptions)
}
